<template>
  <div class="ticket-info-page">
    <SnackbarComponent
      v-model="snackbar.show"
      :message="snackbar.message"
      :color="snackbar.color"
    />
    <!-- TOP: Case header -->
    <div class="ticket-top">
      <div class="d-flex align-start justify-space-between" style="gap: 12px">
        <div class="flex-grow-1">
          <div class="text-h6 font-weight-bold mb-2">{{ ticket.subject }}</div>

          <div class="d-flex align-center flex-wrap header-meta" style="gap: 10px">
            <span class="text-body-2 text-medium-emphasis">
              Case ID: {{ ticket.caseId }}
            </span>

            <span class="meta-dot">•</span>

            <span class="text-body-2 text-medium-emphasis d-inline-flex align-center" style="gap: 6px">
              Severity:
              <v-chip
                size="small"
                variant="flat"
                :color="severityChipColor"
                class="font-weight-bold text-white"
              >
                {{ ticket.severity }}
              </v-chip>
            </span>

            <span class="meta-dot">•</span>

            <v-chip
              size="small"
              variant="flat"
              :color="statusColor"
              class="text-uppercase font-weight-bold text-white"
              :prepend-icon="statusIcon"
            >
              {{ statusLabel }}
            </v-chip>
          </div>
        </div>

        <div class="d-flex align-center flex-wrap flex-shrink-0" style="gap: 8px">
          <v-btn
            v-if="!isResolved && !isAutoClosed"
            variant="outlined"
            color="primary"
            class="text-none"
            :loading="resolving"
            @click="markResolved"
          >
            Mark resolved
          </v-btn>
          <v-btn
            variant="text"
            color="primary"
            prepend-icon="mdi-arrow-left"
            class="text-none"
            @click="$emit('back')"
          >
            Back
          </v-btn>
        </div>
      </div>

      <v-divider class="mt-4" />
    </div>

    <!-- MIDDLE: Correspondence header fixed + messages scroll -->
    <div class="ticket-middle">
      <div class="correspondence-header">
        <div class="text-subtitle-1 font-weight-bold">Correspondence</div>
        <div class="correspondence-header-right">
          <div class="text-body-2 text-medium-emphasis">
            {{ visibleMessages.length }} {{ threadCountLabel }}
          </div>
          <div class="correspondence-actions">
            <v-btn
              variant="text"
              prepend-icon="mdi-chat-processing-outline"
              class="text-none view-logs-btn"
              @click="openLogs"
            >
              View logs
            </v-btn>
            <v-btn
              v-if="showingLogs"
              variant="outlined"
              class="text-none"
              @click="closeLogs"
            >
              Close
            </v-btn>
          </div>
        </div>
      </div>

      <div ref="messagesScroll" class="messages-scroll">
        <div v-if="messagesLoading && !showingLogs" class="empty-correspondence">
          <v-progress-circular indeterminate color="primary" size="32" />
        </div>
        <div v-else-if="visibleMessages.length" class="timeline">
          <div
            v-for="(msg, index) in visibleMessages"
            :key="msg.clientId || msg.id || `${msg.time}-${index}`"
            class="timeline-item"
            :class="{
              'is-last': index === visibleMessages.length - 1,
              'timeline-item--pending': msg.pending,
              'timeline-item--divider': msg.isDivider,
            }"
          >
            <template v-if="msg.isDivider">
              <div class="thread-divider">
                <span>{{ msg.text }}</span>
              </div>
            </template>
            <template v-else>
            <div class="timeline-rail">
              <span
                class="timeline-node"
                :class="{
                  'node-support': msg.type === 'support',
                  'node-bot': msg.type === 'bot',
                }"
              ></span>
              <div v-if="index !== visibleMessages.length - 1" class="timeline-line"></div>
            </div>

            <div class="timeline-content">
              <div class="d-flex align-start" style="gap: 12px">
                <v-avatar :color="avatarColor(msg.type)" size="36" class="flex-shrink-0">
                  <span class="text-white text-caption font-weight-bold">
                    {{ avatarLabel(msg) }}
                  </span>
                </v-avatar>

                <div class="flex-grow-1 min-w-0">
                  <div class="d-flex align-center flex-wrap mb-2" style="gap: 8px">
                    <span class="font-weight-bold">{{ displaySender(msg) }}</span>
                    <v-chip
                      v-if="msg.type === 'bot'"
                      size="x-small"
                      color="warning"
                      variant="tonal"
                    >
                      AI
                    </v-chip>
                    <v-chip
                      v-if="msg.type === 'support'"
                      size="x-small"
                      color="primary"
                      variant="flat"
                    >
                      SUPPORT
                    </v-chip>
                    <span class="text-caption text-medium-emphasis">• {{ msg.time }}</span>
                    <span v-if="msg.pending" class="text-caption sending-label">Sending...</span>
                    <div
                      v-if="canModifyMessage(msg) && editingMessageId !== msg.id"
                      class="message-actions ml-auto"
                    >
                      <v-btn
                        icon
                        size="x-small"
                        variant="text"
                        color="primary"
                        title="Edit message"
                        :disabled="Boolean(editingMessageId) || Boolean(deletingMessageId)"
                        @click="startEditMessage(msg)"
                      >
                        <v-icon size="16">mdi-pencil-outline</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        size="x-small"
                        variant="text"
                        color="error"
                        title="Delete message"
                        :loading="deletingMessageId === msg.id"
                        :disabled="Boolean(editingMessageId) || Boolean(deletingMessageId)"
                        @click="confirmDeleteMessage(msg)"
                      >
                        <v-icon size="16">mdi-delete-outline</v-icon>
                      </v-btn>
                    </div>
                  </div>

                  <div class="message-body">
                    <div
                      v-if="editingMessageId === msg.id"
                      class="message-edit"
                    >
                      <v-textarea
                        v-model="editText"
                        variant="outlined"
                        density="compact"
                        rows="2"
                        auto-grow
                        hide-details
                        autofocus
                        :disabled="savingEdit"
                      />
                      <div class="d-flex justify-end mt-2" style="gap: 8px">
                        <v-btn
                          size="small"
                          variant="outlined"
                          class="text-none"
                          :disabled="savingEdit"
                          @click="cancelEditMessage"
                        >
                          Cancel
                        </v-btn>
                        <v-btn
                          size="small"
                          color="primary"
                          variant="flat"
                          class="text-none"
                          :loading="savingEdit"
                          :disabled="!editText.trim() || editText.trim() === msg.text.trim()"
                          @click="saveEditMessage(msg)"
                        >
                          Save
                        </v-btn>
                      </div>
                    </div>
                    <div
                      v-else-if="msg.text"
                      class="message-bubble"
                      :class="`bubble-${msg.type}`"
                    >
                      <div class="message-text">{{ msg.text }}</div>
                    </div>
                    <div
                      v-if="messageAttachmentUrls(msg).length"
                      class="message-attachments"
                    >
                      <button
                        v-for="(url, attachmentIndex) in messageAttachmentUrls(msg)"
                        :key="`${msg.id || index}-att-${attachmentIndex}`"
                        type="button"
                        class="message-attachment-btn"
                        @click="openAttachmentPreview(url)"
                      >
                        <img
                          v-if="isImageAttachment(url)"
                          :src="url"
                          alt="Ticket attachment"
                          class="message-attachment-image"
                        />
                        <a
                          v-else
                          :href="url"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="message-attachment-link"
                          @click.stop
                        >
                          <v-icon size="16" class="mr-1">mdi-paperclip</v-icon>
                          Attachment {{ attachmentIndex + 1 }}
                        </a>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </template>
          </div>
          <div ref="messagesEnd"></div>
        </div>

        <div v-else class="empty-correspondence">
          <v-icon size="40" color="grey-lighten-1">
            {{ showingLogs ? "mdi-chat-remove-outline" : "mdi-message-outline" }}
          </v-icon>
          <div class="text-body-2 text-medium-emphasis mt-2">
            {{
              showingLogs
                ? "No chatbot conversation is available for this case."
                : "No correspondence yet."
            }}
          </div>
        </div>
      </div>
    </div>

    <!-- BOTTOM: Admin reply + resolved notice -->
    <div class="ticket-bottom">
      <div class="text-subtitle-1 font-weight-bold mb-3">Add correspondence</div>
      <div class="reply-box">
        <div class="reply-compose">
          <div v-if="replyImageAttachments.length" class="reply-compose-attachments">
            <div
              v-for="(item, index) in replyImageAttachments"
              :key="item.id"
              class="reply-attachment-item"
            >
              <button
                type="button"
                class="reply-attachment-btn"
                @click="openAttachmentPreview(item.previewUrl)"
              >
                <img
                  :src="item.previewUrl"
                  alt="Attachment preview"
                  class="reply-attachment-image"
                />
              </button>
              <v-btn
                icon
                size="x-small"
                variant="text"
                class="reply-attachment-remove"
                :disabled="submitting || uploadingImages"
                @click="removeReplyImage(index)"
              >
                <v-icon size="16">mdi-close</v-icon>
              </v-btn>
            </div>
          </div>

          <textarea
            v-model="replyText"
            ref="replyTextarea"
            class="reply-compose-textarea"
            placeholder="Type your response..."
            rows="3"
            :disabled="submitting || uploadingImages"
            @input="autoGrowReplyTextarea"
          ></textarea>
        </div>

        <div class="d-flex align-center justify-space-between flex-wrap mt-3" style="gap: 8px">
          <div>
            <input
              ref="replyImageInput"
              type="file"
              accept="image/*"
              multiple
              class="d-none"
              @change="onReplyImagesSelected"
            />
            <v-btn
              variant="outlined"
              prepend-icon="mdi-image-plus"
              :disabled="submitting || uploadingImages"
              @click="$refs.replyImageInput?.click()"
            >
              Upload image
            </v-btn>
          </div>
          <div class="d-flex" style="gap: 8px">
            <v-btn
              variant="outlined"
              :disabled="submitting || uploadingImages"
              @click="clearReplyForm"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              variant="flat"
              :disabled="!canSubmit"
              :loading="submitting || uploadingImages"
              @click="submitReply"
            >
              Submit
            </v-btn>
          </div>
        </div>
      </div>

      <div
        v-if="isResolved || isAutoClosed"
        class="resolved-bar mt-4"
        :class="{ 'auto-closed-bar': isAutoClosed }"
      >
        <div class="text-body-2">
          <template v-if="isAutoClosed">
            <strong>Auto-closed</strong> — this case was closed due to inactivity. Only the
            reporter can reopen it from their side.
          </template>
          <template v-else>
            <strong>Marked Resolved</strong>
          </template>
        </div>
      </div>
    </div>

    <v-dialog v-model="deleteDialog" max-width="420" persistent>
      <v-card rounded="lg">
        <v-card-title class="text-h6">Delete message?</v-card-title>
        <v-card-text>
          This message will be removed from the correspondence. This action cannot be undone.
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer />
          <v-btn
            variant="text"
            class="text-none"
            :disabled="Boolean(deletingMessageId)"
            @click="closeDeleteDialog"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            class="text-none"
            :loading="Boolean(deletingMessageId)"
            @click="deleteMessage"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <Teleport to="body">
      <div
        v-if="previewAttachmentUrl"
        class="attachment-preview-overlay"
        @click.self="closeAttachmentPreview"
      >
        <button
          type="button"
          class="attachment-preview-close"
          aria-label="Close"
          @click="closeAttachmentPreview"
        >
          <v-icon color="white" size="22">mdi-close</v-icon>
        </button>
        <img
          :src="previewAttachmentUrl"
          alt="Attachment preview"
          class="attachment-preview-full"
        />
      </div>
    </Teleport>
  </div>
</template>

<script>
import { generateClient } from "aws-amplify/api";
import { getUrl, uploadData } from "@aws-amplify/storage";
import {
  add_correspondence,
  edit_correspondence,
  edit_ticket,
  get_upload_url,
} from "@/graphql/mutations";
import { displayStatus, formatDate, normalizeChatbotLogs } from "@/mixins/RCSSupport/TicketApi";
import TicketDetailApi from "@/mixins/RCSSupport/TicketDetailApi";
import SnackbarComponent from "@/components/Extras/Snackbar.vue";

const client = generateClient();

export default {
  name: "CaseReply",
  mixins: [TicketDetailApi],
  components: {
    SnackbarComponent,
  },
  props: {
    ticket: {
      type: Object,
      required: true,
    },
  },
  emits: ["back", "updated", "deleted"],
  data() {
    return {
      replyText: "",
      messages: [],
      messagesLoading: false,
      loadedTicketId: "",
      replyImageAttachments: [],
      submitting: false,
      uploadingImages: false,
      resolving: false,
      maxReplyImages: 5,
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
      previewAttachmentUrl: "",
      showingLogs: false,
      editingMessageId: "",
      editText: "",
      savingEdit: false,
      deleteDialog: false,
      messageToDelete: null,
      deletingMessageId: "",
    };
  },
  computed: {
    isResolved() {
      return this.statusLabel === "Resolved";
    },
    isAutoClosed() {
      return this.statusLabel === "Auto-closed";
    },
    statusLabel() {
      return displayStatus(this.ticket?.status || this.ticket?.statusRaw);
    },
    statusColor() {
      const map = {
        "Awaiting RCS Support": "info",
        "Awaiting your reply": "warning",
        "PENDING_CUSTOMER_ACTION": "warning",
        Resolved: "success",
        "Auto-closed": "grey",
        Open: "info",
        "In progress": "warning",
      };
      return map[this.statusLabel] || "primary";
    },
    statusIcon() {
      if (this.isResolved) return "mdi-check";
      if (this.isAutoClosed) return "mdi-timer-off-outline";
      if (
        this.statusLabel === "Awaiting your reply" ||
        this.statusLabel === "In progress"
      ) {
        return "mdi-progress-clock";
      }
      return "mdi-circle-outline";
    },
    severityChipColor() {
      const map = {
        Critical: "error",
        High: "warning",
        Normal: "success",
        Low: "grey",
      };
      return map[this.ticket?.severity] || "grey";
    },
    supportSender() {
      const user = this.$store?.state?.user || {};
      return (
        user.email ||
        user.user_email_id ||
        user.username ||
        user.name ||
        "Support"
      );
    },
    canSubmit() {
      const hasContent = Boolean(
        this.replyText.trim() || this.replyImageAttachments.length
      );
      return !this.submitting && !this.uploadingImages && hasContent;
    },
    logUserName() {
      const name =
        this.ticket?.reporter_name ||
        this.ticket?.reporterName ||
        this.ticket?.reporter ||
        this.ticket?.reporter_email ||
        "User";
      return String(name).trim() || "User";
    },
    logMessages() {
      const fromLogs = this.messagesFromLogs();
      if (fromLogs.length) return fromLogs;
      return this.messagesFromSummary();
    },
    visibleMessages() {
      if (!this.showingLogs) return this.messages;
      const logs = this.logMessages;
      if (!logs.length) return this.messages;
      if (!this.messages.length) return logs;
      return [
        ...logs,
        { id: "logs-divider", isDivider: true, text: "Correspondence" },
        ...this.messages,
      ];
    },
    threadCountLabel() {
      const count = this.showingLogs
        ? this.logMessages.length + this.messages.length
        : this.messages.length;
      return count === 1 ? "entry" : "entries";
    },
  },
  watch: {
    ticket: {
      immediate: true,
      async handler(val) {
        const ticketId = val?.ticketId || val?.ticket_id || "";
        if (!ticketId) {
          this.loadedTicketId = "";
          this.messages = [];
          this.showingLogs = false;
          this.clearReplyForm();
          return;
        }

        if (ticketId !== this.loadedTicketId) {
          this.loadedTicketId = ticketId;
          this.showingLogs = false;
          this.clearReplyForm();
          this.cancelEditMessage();
          this.messagesLoading = true;
          try {
            this.messages = this.activeMessages(val?.messages);
            await this.resolveMessageAttachments();
            this.scrollToLatest();
          } finally {
            this.messagesLoading = false;
          }
        }
      },
    },
    messages() {
      if (!this.showingLogs) this.scrollToLatest();
    },
  },
  beforeUnmount() {
    this.clearReplyImages();
  },
  methods: {
    openLogs() {
      if (this.showingLogs) return;
      this.showingLogs = true;
      this.$nextTick(() => this.scrollToTop());
    },
    closeLogs() {
      if (!this.showingLogs) return;
      this.showingLogs = false;
      this.$nextTick(() => this.scrollToLatest());
    },
    scrollToTop() {
      const scroller = this.$refs.messagesScroll;
      if (scroller) {
        scroller.scrollTop = 0;
      }
    },
    formatLogTime(value) {
      if (!value) return formatDate(value);
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return formatDate(value);
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    },
    messagesFromLogs() {
      const logs = normalizeChatbotLogs(
        this.ticket?.chatbotLogs || this.ticket?.chatbot_logs
      )
        .slice()
        .sort((a, b) => Number(a.created_on || 0) - Number(b.created_on || 0));

      const messages = [];
      logs.forEach((log, index) => {
        const time = this.formatLogTime(log.created_on);
        const logId = log.log_id || index;
        const query = String(log.query || "").trim();
        const response = String(log.response || "").trim();

        if (query) {
          messages.push({
            id: `${logId}-query`,
            type: "user",
            sender: this.logUserName,
            text: query,
            time,
          });
        }
        if (response) {
          messages.push({
            id: `${logId}-response`,
            type: "bot",
            sender: "RCS Support Bot",
            text: response,
            time,
          });
        }
      });
      return messages;
    },
    messagesFromSummary() {
      const summary = String(
        this.ticket?.aiOriginSummary || this.ticket?.ai_origin_summary || ""
      ).trim();
      if (!summary) return [];

      const chunks = summary.split(/\n(?=(?:Assistant|User)\s*:)/i);
      return chunks
        .map((chunk, index) => {
          const match = chunk.match(/^(Assistant|User)\s*:\s*([\s\S]*)$/i);
          if (!match) return null;
          const type = match[1].toLowerCase() === "assistant" ? "bot" : "user";
          const text = String(match[2] || "").trim();
          if (!text) return null;
          return {
            id: `summary-${index}`,
            type,
            sender: type === "bot" ? "RCS Support Bot" : this.logUserName,
            text,
            time: this.formatLogTime(
              this.ticket?.created_on || this.ticket?.createdOn
            ),
          };
        })
        .filter(Boolean);
    },
    scrollToLatest() {
      this.$nextTick(() => {
        const end = this.$refs.messagesEnd;
        if (end?.scrollIntoView) {
          end.scrollIntoView({ block: "end", behavior: "smooth" });
          return;
        }
        const scroller = this.$refs.messagesScroll;
        if (scroller) {
          scroller.scrollTop = scroller.scrollHeight;
        }
      });
    },
    displaySender(msg) {
      const sender = String(msg?.sender || "").trim();
      if (sender && sender.toLowerCase() !== "unknown") return sender;
      if (msg?.type === "support") return this.supportSender;
      return sender || "Unknown";
    },
    avatarLabel(msg) {
      if (msg.type === "bot") return "AI";
      return (this.displaySender(msg) || "?").charAt(0).toUpperCase();
    },
    avatarColor(type) {
      if (type === "bot") return "warning";
      if (type === "support") return "primary";
      return "grey";
    },
    parseApiJson(value, fallback = {}) {
      let current = value;
      for (let i = 0; i < 4; i += 1) {
        if (typeof current !== "string") break;
        const trimmed = current.trim();
        if (!trimmed) break;
        try {
          current = JSON.parse(trimmed);
        } catch {
          break;
        }
      }
      if (current && typeof current === "object") return current;
      return fallback;
    },
    extractS3Key(pathOrUrl) {
      const value = String(pathOrUrl || "").trim();
      if (!value) return "";
      if (value.startsWith("http")) {
        try {
          const parsed = new URL(value);
          return decodeURIComponent(parsed.pathname.replace(/^\/+/, ""));
        } catch (_) {
          return value.split("?")[0].replace(/^\/+/, "");
        }
      }
      return value.split("?")[0].replace(/^\/+/, "");
    },
    isImageAttachment(url) {
      if (!url) return false;
      const value = String(url);
      if (value.startsWith("blob:") || value.startsWith("data:image")) return true;
      const cleanUrl = value.split("?")[0].toLowerCase();
      return (
        /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(cleanUrl) ||
        /ticket-attachments/i.test(value)
      );
    },
    normalizeAttachmentUrls(attachments) {
      if (!attachments) return [];
      let list = attachments;
      if (typeof attachments === "string") {
        const parsed = this.parseApiJson(attachments, null);
        list = Array.isArray(parsed) ? parsed : [attachments];
      }
      list = Array.isArray(list) ? list : [list];
      return list
        .map((item) => {
          if (!item) return "";
          if (typeof item === "string") return item.trim();
          return String(
            item.url ||
              item.file_url ||
              item.fileUrl ||
              item.presigned_url ||
              item.presignedUrl ||
              item.path ||
              item.key ||
              ""
          ).trim();
        })
        .filter(Boolean); // remove empty strings
    },
    messageAttachmentUrls(msg) {
      return this.normalizeAttachmentUrls(msg?.attachments || []);
    },
    async resolveAttachmentUrl(pathOrUrl) {
      const value = String(pathOrUrl || "").trim();
      if (!value) return "";
      if (
        value.startsWith("http") ||
        value.startsWith("blob:") ||
        value.startsWith("data:")
      ) {
        return value;
      }
      const path = this.extractS3Key(value);
      for (const bucket of ["bms", "landing"]) {
        try {
          const result = await getUrl({
            path,
            options: { bucket },
          });
          return result?.url?.href || result?.url || value;
        } catch (_) {
          // try next bucket
        }
      }
      return value;
    },
    async resolveMessageAttachments() {
      await Promise.all(
        this.messages.map(async (msg) => {
          const urls = this.normalizeAttachmentUrls(msg?.attachments || []);
          msg.attachments = await Promise.all(
            urls.map((url) => this.resolveAttachmentUrl(url))
          );
        })
      );
    },
    openAttachmentPreview(url) {
      if (!url) return;
      const value = String(url);
      if (this.isImageAttachment(value) || value.startsWith("blob:")) {
        this.previewAttachmentUrl = value;
      }
    },
    closeAttachmentPreview() {
      this.previewAttachmentUrl = "";
    },
    autoGrowReplyTextarea(event) {
      const el = event?.target || this.$refs.replyTextarea;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    },
    resetReplyTextareaHeight() {
      const el = this.$refs.replyTextarea;
      if (!el) return;
      el.style.height = "auto";
    },
    clearReplyImages() {
      this.replyImageAttachments.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
      this.replyImageAttachments = [];
      if (this.$refs.replyImageInput) {
        this.$refs.replyImageInput.value = "";
      }
    },
    clearReplyForm() {
      this.replyText = "";
      this.clearReplyImages();
      this.$nextTick(() => this.resetReplyTextareaHeight());
    },
    onReplyImagesSelected(event) {
      const files = Array.from(event?.target?.files || []);
      if (!files.length) return;

      for (const file of files) {
        if (!String(file?.type || "").startsWith("image/")) {
          this.snackbar = {
            show: true,
            message: "Only image files can be attached.",
            color: "error",
          };
          continue;
        }
        if (this.replyImageAttachments.length >= this.maxReplyImages) {
          this.snackbar = {
            show: true,
            message: `You can attach up to ${this.maxReplyImages} images.`,
            color: "warning",
          };
          break;
        }
        this.replyImageAttachments.push({
          id: `${Date.now()}-${Math.random()}`,
          file,
          previewUrl: URL.createObjectURL(file),
          name: file.name,
        });
      }

      if (event?.target) {
        event.target.value = "";
      }
    },
    removeReplyImage(index) {
      const [removed] = this.replyImageAttachments.splice(index, 1);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
    },
    extractUploadConfig(responseData) {
      const data = responseData?.data || responseData;
      if (!data) return null;

      if (typeof data === "string") {
        return {
          uploadUrl: data.startsWith("http") ? data : "",
          path: data.startsWith("http") ? this.extractS3Key(data) : data,
          contentType: "",
        };
      }

      const amplifyUpload = data?.amplify_upload || {};
      return {
        uploadUrl:
          data.upload_url ||
          data.uploadUrl ||
          data.presigned_url ||
          data.presignedUrl ||
          data.url ||
          "",
        path:
          amplifyUpload.path ||
          data.s3_key ||
          data.s3Key ||
          data.file_key ||
          data.fileKey ||
          data.path ||
          data.key ||
          "",
        contentType:
          amplifyUpload.contentType ||
          data.content_type ||
          data.contentType ||
          "",
        metadata: amplifyUpload.metadata,
      };
    },
    async getImageUploadConfig(file) {
      const response = await client.graphql({
        query: get_upload_url,
        variables: {
          input: {
            file_name: file.name,
          },
        },
      });

      const result = this.parseApiJson(response.data.get_upload_url, {});
      if (result.status && result.status !== "SUCCESS") {
        throw new Error(result.status_message || "Failed to get upload URL");
      }

      const config = this.extractUploadConfig(result);
      if (!config?.uploadUrl && !config?.path) {
        throw new Error("Upload configuration was not returned");
      }
      return config;
    },
    async uploadImageFile(file) {
      const config = await this.getImageUploadConfig(file);

      if (config.uploadUrl) {
        const headers = {
          "Content-Type": file.type || config.contentType || "application/octet-stream",
        };
        let response = await fetch(config.uploadUrl, {
          method: "PUT",
          body: file,
          headers,
        });
        if (!response.ok) {
          response = await fetch(config.uploadUrl, {
            method: "PUT",
            body: file,
          });
        }
        if (!response.ok) {
          throw new Error(`Failed to upload ${file.name}`);
        }
        return this.extractS3Key(config.path || config.uploadUrl) || config.path;
      }

      const { result } = await uploadData({
        path: config.path,
        data: file,
        options: {
          bucket: "bms",
          contentType: file.type || config.contentType || "image/jpeg",
          metadata: config.metadata,
        },
      });
      return result?.path || config.path;
    },
    async uploadReplyImages(attachments = []) {
      if (!attachments.length) return [];
      this.uploadingImages = true;
      try {
        const uploadedKeys = [];
        for (const attachment of attachments) {
          const uploadKey = await this.uploadImageFile(attachment.file);
          if (uploadKey) uploadedKeys.push(uploadKey);
        }
        return uploadedKeys;
      } finally {
        this.uploadingImages = false;
      }
    },
    removeOptimisticMessage(clientId) {
      this.messages = this.messages.filter((msg) => msg.clientId !== clientId);
    },
    async reloadTicketMessages() {
      const ticketId = this.ticket?.ticketId || this.ticket?.ticket_id;
      if (!ticketId) return null;
      const fullTicket = await this.apiGetTicket(ticketId);
      this.messages = this.activeMessages(fullTicket.messages);
      await this.resolveMessageAttachments();
      return {
        ...this.ticket,
        ...fullTicket,
        messages: [...this.messages],
      };
    },
    async submitReply() {
      const text = this.replyText.trim();
      if ((!text && !this.replyImageAttachments.length) || this.submitting) return;

      const previousImages = [...this.replyImageAttachments];
      const pendingAttachments = previousImages.map((item) => item.previewUrl);
      const clientId = `temp-${Date.now()}`;
      this.messages.push({
        clientId,
        type: "support",
        sender: this.supportSender,
        time: "Just now",
        text,
        attachments: pendingAttachments,
        pending: true,
      });
      this.replyText = "";
      this.replyImageAttachments = [];
      this.$nextTick(() => this.resetReplyTextareaHeight());
      this.scrollToLatest();

      this.submitting = true;
      try {
        const attachments = await this.uploadReplyImages(previousImages);
        const input = {
          ticket_id: this.ticket.ticketId,
          message: text || " ",
        };
        if (attachments.length) {
          input.attachments = attachments;
        }

        const response = await client.graphql({
          query: add_correspondence,
          variables: { input },
        });
        const result = this.parseApiJson(response.data?.add_correspondence, {});
        if (result.status !== "SUCCESS") {
          this.removeOptimisticMessage(clientId);
          this.replyText = text;
          this.replyImageAttachments = previousImages;
          this.snackbar = {
            show: true,
            message: result.status_message || "Failed to send message",
            color: "error",
          };
          return;
        }

        previousImages.forEach((item) => {
          if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        });

        try {
          const updatedTicket = await this.reloadTicketMessages();
          this.$emit("updated", {
            ...(updatedTicket || this.ticket),
            updated: "Just now",
            status: "In progress",
            statusRaw: "PENDING_CUSTOMER_ACTION",
            statusKey: "awaiting_user",
            assignedTo:
              this.ticket.assignedTo && this.ticket.assignedTo !== "-"
                ? this.ticket.assignedTo
                : this.supportSender,
          });
        } catch (reloadError) {
          console.error("Failed to reload correspondence:", reloadError);
          const message = this.messages.find((msg) => msg.clientId === clientId);
          if (message) {
            message.pending = false;
            message.attachments = attachments.length
              ? await Promise.all(
                  attachments.map((key) => this.resolveAttachmentUrl(key))
                )
              : pendingAttachments;
          }
          this.$emit("updated", {
            ...this.ticket,
            messages: [...this.messages],
            updated: "Just now",
            status: "In progress",
            statusRaw: "PENDING_CUSTOMER_ACTION",
            statusKey: "awaiting_user",
            assignedTo:
              this.ticket.assignedTo && this.ticket.assignedTo !== "-"
                ? this.ticket.assignedTo
                : this.supportSender,
          });
        }
      } catch (error) {
        console.error("Failed to submit reply:", error);
        this.removeOptimisticMessage(clientId);
        this.replyText = text;
        this.replyImageAttachments = previousImages;
        this.snackbar = {
          show: true,
          message:
            error?.errors?.[0]?.message ||
            error.message ||
            "Failed to add correspondence",
          color: "error",
        };
      } finally {
        this.submitting = false;
      }
    },
    activeMessages(list) {
      return (Array.isArray(list) ? list : []).filter((msg) => {
        const status = String(
          msg?.raw?.correspondence_status || msg?.raw?.status || ""
        ).toUpperCase();
        return status !== "DEACTIVE";
      });
    },
    canModifyMessage(msg) {
      return (
        !this.showingLogs &&
        msg?.type === "support" &&
        Boolean(msg?.id) &&
        !msg?.pending
      );
    },
    startEditMessage(msg) {
      this.editingMessageId = msg.id;
      this.editText = msg.text || "";
    },
    cancelEditMessage() {
      this.editingMessageId = "";
      this.editText = "";
    },
    async runEditCorrespondence(input, fallbackError) {
      const response = await client.graphql({
        query: edit_correspondence,
        variables: {
          input: {
            ticket_id: this.ticket.ticketId,
            ...input,
          },
        },
      });
      const result = this.parseApiJson(response.data?.edit_correspondence, {});
      if (result.status !== "SUCCESS") {
        throw new Error(result.status_message || fallbackError);
      }
      return result;
    },
    emitMessagesUpdated() {
      this.$emit("updated", {
        ...this.ticket,
        messages: [...this.messages],
      });
    },
    async saveEditMessage(msg) {
      const text = this.editText.trim();
      if (!text || this.savingEdit) return;

      this.savingEdit = true;
      try {
        const result = await this.runEditCorrespondence(
          { correspondence_id: msg.id, message: text },
          "Failed to update message"
        );
        const target = this.messages.find((item) => item.id === msg.id);
        if (target) target.text = text;
        this.cancelEditMessage();
        this.emitMessagesUpdated();
        this.snackbar = {
          show: true,
          message: result.status_message || "Message updated",
          color: "success",
        };
      } catch (error) {
        console.error("Failed to edit correspondence:", error);
        this.snackbar = {
          show: true,
          message:
            error?.errors?.[0]?.message ||
            error.message ||
            "Failed to update message",
          color: "error",
        };
      } finally {
        this.savingEdit = false;
      }
    },
    confirmDeleteMessage(msg) {
      this.messageToDelete = msg;
      this.deleteDialog = true;
    },
    closeDeleteDialog() {
      this.deleteDialog = false;
      this.messageToDelete = null;
    },
    async deleteMessage() {
      const msg = this.messageToDelete;
      if (!msg?.id || this.deletingMessageId) return;

      this.deletingMessageId = msg.id;
      try {
        const result = await this.runEditCorrespondence(
          { correspondence_id: msg.id, correspondence_status: "DEACTIVE" },
          "Failed to delete message"
        );
        this.messages = this.messages.filter((item) => item.id !== msg.id);
        this.closeDeleteDialog();
        this.emitMessagesUpdated();
        this.snackbar = {
          show: true,
          message: result.status_message || "Message deleted",
          color: "success",
        };
      } catch (error) {
        console.error("Failed to delete correspondence:", error);
        this.snackbar = {
          show: true,
          message:
            error?.errors?.[0]?.message ||
            error.message ||
            "Failed to delete message",
          color: "error",
        };
      } finally {
        this.deletingMessageId = "";
      }
    },
    async markResolved() {
      this.resolving = true;
      try {
        const response = await client.graphql({
          query: edit_ticket,
          variables: {
            input: {
              ticket_id: this.ticket.ticketId,
              ticket_status: "RESOLVED",
            },
          },
        });
        const raw = response.data.edit_ticket;
        const result = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (result.status !== "SUCCESS") {
          throw new Error(result.status_message || "Failed to mark resolved");
        }

        this.messages.push({
          type: "support",
          sender: this.supportSender,
          time: "Just now",
          text: "Case marked as resolved.",
        });

        this.$emit("updated", {
          ...this.ticket,
          messages: [...this.messages],
          status: "Resolved",
          statusRaw: "RESOLVED",
          statusKey: "resolved",
          updated: "Just now",
        });
        this.snackbar = {
          show: true,
          message: "The case has been resolved",
          color: "success",
        };
      } catch (error) {
        console.error("Failed to mark resolved:", error);
        this.snackbar = {
          show: true,
          message: error.message || "Failed to mark the case as resolved",
          color: "error",
        };
      } finally {
        this.resolving = false;
      }
    },
  },
};
</script>

<style scoped>
.ticket-info-page {
  height: calc(100vh - 32px);
  max-height: calc(100vh - 32px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 4px 8px 12px;
  box-sizing: border-box;
}

.ticket-top {
  flex-shrink: 0;
}

.ticket-middle {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  margin-top: 12px;
  overflow: hidden;
}

.correspondence-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.correspondence-header-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-left: auto;
}

.correspondence-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.view-logs-btn {
  color: blue !important;
}

.view-logs-btn :deep(.v-icon) {
  color: bluex !important;
}

.messages-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
  padding-bottom: 12px;
}

.ticket-bottom {
  flex-shrink: 0;
  margin-top: auto;
  padding-top: 20px;
  padding-bottom: 8px;
  border-top: 1px solid #eceff3;
  background: #fff;
}

.meta-dot {
  color: rgba(0, 0, 0, 0.3);
}

.timeline {
  display: flex;
  flex-direction: column;
}

.timeline-item {
  display: flex;
  gap: 10px;
}

.timeline-item--divider {
  display: block;
  padding: 4px 0 16px;
}

.thread-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
  font-weight: 600;
}

.thread-divider::before,
.thread-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #eceff3;
}

.timeline-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 14px;
  flex-shrink: 0;
  padding-top: 12px;
}

.timeline-node {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid #b0b8c4;
  background: #fff;
  z-index: 1;
}

.timeline-node.node-support {
  border-color: #1976d2;
  background: #1976d2;
}

.timeline-node.node-bot {
  border-color: #fb8c00;
  background: #fb8c00;
}

.timeline-line {
  width: 2px;
  flex: 1;
  min-height: 24px;
  background: #d7dde7;
  margin-top: 4px;
}

.timeline-content {
  flex: 1;
  padding-bottom: 16px;
  min-width: 0;
}

.timeline-item.is-last .timeline-content {
  padding-bottom: 8px;
}

.timeline-item--pending .message-body {
  opacity: 0.88;
}

.sending-label {
  color: rgba(25, 118, 210, 0.85);
  font-style: italic;
}

.message-bubble {
  min-width: 0;
  border: 1px solid #e6e8ee;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 14px;
  line-height: 1.55;
  color: rgba(0, 0, 0, 0.78);
  background: #fff;
}

.message-text {
  white-space: pre-wrap;
}

.message-body {
  min-width: 0;
}

.message-actions {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.bubble-support {
  background: #f5f9ff;
  border-color: #cfe0f5;
}

.bubble-bot {
  background: #fff8f0;
  border-color: #ffe0b2;
}

.message-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 12px;
}

.message-attachment-btn {
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  display: block;
}

.message-attachment-image {
  width: 280px;
  max-width: 100%;
  height: 180px;
  object-fit: cover;
  border-radius: 16px;
  border: 1px solid #eceff3;
  display: block;
  background: #f7f8fb;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
}

.message-attachment-link {
  display: inline-flex;
  align-items: center;
  color: #1565c0;
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;
}

.empty-correspondence {
  text-align: center;
  padding: 40px 16px;
}

.reply-box {
  border: 1px solid #e6e8ee;
  border-radius: 12px;
  padding: 14px;
  background: #fff;
}

.reply-compose {
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 12px;
  background: #fff;
  min-height: 96px;
}

.reply-compose-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 10px;
}

.reply-compose-textarea {
  width: 100%;
  display: block;
  border: 0;
  outline: none;
  resize: none;
  min-height: 72px;
  padding: 0;
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: rgba(0, 0, 0, 0.87);
  background: transparent;
  font-family: inherit;
}

.reply-compose-textarea::placeholder {
  color: rgba(0, 0, 0, 0.45);
}

.reply-compose-textarea:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.reply-attachment-item {
  position: relative;
}

.reply-attachment-btn {
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  display: block;
}

.reply-attachment-image {
  width: 88px;
  height: 88px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  display: block;
  background: #fafafa;
}

.reply-attachment-remove {
  position: absolute;
  top: 2px;
  right: 2px;
  background: rgba(255, 255, 255, 0.9) !important;
}

.attachment-preview-overlay {
  position: fixed;
  inset: 0;
  z-index: 20000;
  background: rgba(0, 0, 0, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.attachment-preview-full {
  max-width: min(92vw, 1100px);
  max-height: 88vh;
  object-fit: contain;
  border-radius: 8px;
}

.attachment-preview-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.resolved-bar {
  border: 1px solid #c8e6c9;
  background: #f1f8f2;
  border-radius: 14px;
  padding: 18px 20px;
}

.auto-closed-bar {
  border-color: #cfd8dc;
  background: #f5f7f8;
}

.min-w-0 {
  min-width: 0;
}

@media (max-width: 600px) {
  .header-meta .meta-dot {
    display: none;
  }

  .ticket-info-page {
    height: calc(100vh - 24px);
    max-height: calc(100vh - 24px);
  }
}
</style>
